<script setup lang="ts">
import { computed, inject } from 'vue'
import type { SidebarMenuItem } from '@/types/sidebar.types'
import { SIDEBAR_INJECTION_KEY } from '@/types/sidebar.types'
import JIcon from '@/components/atoms/JIcon.vue'
import JTooltip from '@/components/atoms/JTooltip.vue'
import { cn } from '@/lib/utils'

/**
 * JSidebarItem - 메뉴 아이템 (menuType='L' 전용)
 * 링크 타입 메뉴만 렌더링. 폴더(F)는 JSidebarGroup에서 처리.
 */

const props = defineProps<{
  item: SidebarMenuItem
}>()

const emit = defineEmits<{
  'menu-click': [item: SidebarMenuItem, event: MouseEvent]
}>()

const state = inject(SIDEBAR_INJECTION_KEY)!

const isActive = computed(() => {
  if (!props.item.path || !state.activePath) return false
  return state.activePath === props.item.path
})

const isFav = computed(() => state.favorites.has(props.item.id))

const handleClick = (event: MouseEvent) => {
  if (props.item.disabled) return
  emit('menu-click', props.item, event)
}

const handleFavoriteClick = (event: MouseEvent) => {
  event.stopPropagation()
  state.toggleFavorite(props.item.id)
}
</script>

<template>
  <!-- Collapsed: 아이콘 + Tooltip -->
  <JTooltip
    v-if="state.collapsed"
    :content="item.label"
    side="right"
    size="sm"
  >
    <template #trigger>
      <div
        :class="cn(
          'flex items-center justify-center py-1.5 mx-1 rounded-md cursor-pointer transition-colors',
          isActive
            ? 'bg-primary/10 text-primary'
            : 'text-foreground hover:bg-accent/50',
          item.disabled && 'opacity-40 cursor-not-allowed'
        )"
        @click="handleClick"
      >
        <JIcon :name="item.icon || 'file'" size="sm" />
      </div>
    </template>
  </JTooltip>

  <!-- Expanded: 아이콘 + 라벨 + 즐겨찾기 -->
  <div
    v-else
    :class="cn(
      'group flex items-center gap-1.5 py-0.5 px-2 rounded-md cursor-pointer transition-colors relative',
      isActive
        ? 'bg-primary/10 text-primary font-medium'
        : 'text-foreground hover:bg-accent/50',
      item.disabled && 'opacity-40 cursor-not-allowed'
    )"
    @click="handleClick"
  >
    <!-- 활성 좌측 바 -->
    <div
      v-if="isActive"
      class="absolute left-0 top-1 bottom-1 w-[3px] rounded-r bg-primary"
    />

    <JIcon
      v-if="item.icon"
      :name="item.icon"
      size="sm"
      class="flex-shrink-0"
    />
    <span class="flex-1 truncate text-[11px]">{{ item.label }}</span>

    <!-- 즐겨찾기 별 -->
    <button
      :class="cn(
        'flex-shrink-0 p-0.5 rounded transition-opacity',
        isFav
          ? 'opacity-100 text-yellow-500'
          : 'opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-yellow-500'
      )"
      @click="handleFavoriteClick"
    >
      <JIcon
        name="star"
        size="sm"
        :class="isFav ? 'fill-yellow-500' : undefined"
      />
    </button>
  </div>
</template>
